import {
  Box,
  Stack,
  Typography,
  Button,
} from "@mui/material";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useSnackbar } from "notistack";
import { getDepartments, getJobTitles, getAccounts, saveOrEditPayrollEmpMapping, getDeptJobDetails, getEmpName, getProfitCenters, getPayrollItemNames, getBillEntryPreferences } from "../../../../api";
import PayrollsPopup from "../../../../Components/PayrollPopup";
import { useAlerts, usePermissions } from "../../../../Hooks";
import AddNewEmployee from "../../../../Pages/EmployeeManagement/AddNewEmployee/AddNewEmployee";
import AddJobTitle from "../../../../QuickAdd/AddJobTitle";
import { buildUpdatePayrollEmployeeMappingPayload } from "../../../../Payloads/EntryPayload";
import PayPeriodFormInfo from "../../../../Components/PayPeriodFormInfo";
import EmployeeAddDeductTaxTables from "../../../../Components/EmployeeAddDeductTaxTables";
import { Loader } from "../../../../Components/Loader";
import EmployeeWageMappingTable from "../../../../Components/EmployeeWageMappingTable";
import ArrowRightTwoToneIcon from '@mui/icons-material/ArrowRightTwoTone';
import ArrowLeftTwoToneIcon from '@mui/icons-material/ArrowLeftTwoTone';
import AddNewPayroll from "../../../PayrollSetup/AddNew/AddNewPayroll";
import { useFormikContext } from "formik";
import ASKAIpopup from "../../../../Components/AskAIPopup";
import { useParams } from "react-router-dom";
import { ITEM_TYPE } from "../../../../Enums/Enum";
import { usePayrollEmployeeDetails, usePayrollAttachments, usePayrollActions, useResizablePanel } from "../../../../Hooks/PayrollFormHooks";
// --- Helpers & Formatters ---
import { formatHours, formatAmount, parseHoursToDecimal, mapEmployeeNameOptions, fetchAISuggestions, applyAISuggestions } from "../../../../HelperFunctions/PayrollEntryHelpers";
import { PayrollFormProvider } from "../../../../Context/PayrollFormContext";
import {
  useEmployeePayrollMappingActions,
  EmployeePayrollMappingActionButtons,
} from "./EmployeePayrollMappingActions";

export default function EmployeePayrollMappingForm({
  onClose,
  setStep,
  row,
  setSelectedRow,
  accountOptions: propAccountOptions = [],
  payrollId: propPayrollId,
  corporationId: propCorporationId,
}) {

  useEffect(() => {
    // When this view is active, ensure the main application menu is hidden.
    window.parent.postMessage("HideMenu", "*");

    // The parent component (PayPeriodSetup) is responsible for showing the menu on final close.
    // So, no cleanup is needed here.
  }, []);
  // --- Global state hooks ---
  const { menuId, popupID, payrollEmpId } = useParams();
  const { permissions } = usePermissions(menuId);

  const { values, setFieldValue, setFieldError, setFieldTouched } = useFormikContext();
  const valuesRef = useRef(values);
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  const hasCalledUnmatchedQueueRef = useRef(false);
  const shouldFetchDeptJobRef = useRef(null);
  const skipAskAiOnEmployeeChangeRef = useRef(false);

  const [formValues, setFormValues] = useState({
    name: row?.name || row?.raw?.EmployeeName || "",
    department: row?.raw?.DepartmentID || row?.raw?.IncDeptID || "",
    job: row?.raw?.JobID || row?.raw?.JobId || "",
    profitCenter: row?.raw?.PCID || row?.raw?.ProfitCenterID || row?.profitCenter || "",
    checkNo: "",
    netPay: 0,
    employeeId: row?.raw?.EmployeeID || row?.raw?.EmployeeId || "",
    isVoidStamp: row?.raw?.IsActive === 2,
    reconStatus: row?.raw?.ReconStatus || false,
    clearedDate: row?.raw?.ClearedDate || null,
    importedName: row?.raw?.ImportedEmployeeName ?? row?.importedName ?? "",
    importedDept: "",
    importedJob: "",
    importedNetPay: 0,
    corporationName: "",
    payFrequencyDate: "",
    payFrequency: "",
    payDate: null,
    bankAccountID: "", // This is BankAccountID
    bankAccount: "", // This is BankAccountName or BankAccount
    corporationID: "",
    frequencyID: "",
  });
  const [isDropdownSelected, setIsDropdownSelected] = useState(false);
  const [manualDeptJobChange, setManualDeptJobChange] = useState(0);
  const lastManualDeptJobChangeRef = useRef(0);
  const lastChangedFieldRef = useRef(null);

  const formValuesRef = useRef(formValues);
  useEffect(() => {
    formValuesRef.current = formValues;
  }, [formValues]);

  const apiRatesRef = useRef({});
  const [billEntryPreferences, setBillEntryPreferences] = useState({
    voidDate: false,
    previewPanel: false,
  });

  const originalFormValuesRef = useRef({ department: "", job: "", profitCenter: "", isInitialDeptJobNull: false });
  const lastIsDeptJobRef = useRef(false);
  const prevTableValuesRef = useRef(null);

  const [empPayrollDetails, setEmpPayrollDetails] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  // const [loadingDeptJob, setLoadingDeptJob] = useState(false);
  const [isVoid, setIsVoid] = useState(false);
  const [voidDate, setVoidDate] = useState(values?.payDate ? dayjs(values.payDate) : dayjs());
  const [comments, setComments] = useState("");
  const [askAiPopupOpen, setAskAiPopupOpen] = useState(false);
  const [askAiButtonVisible, setAskAiButtonVisible] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [profitCenters, setProfitCenters] = useState([]);
  const { popup: alertPopup, showCancelConfirm, hidePopup, showDeleteConfirm } = useAlerts();
  const { enqueueSnackbar } = useSnackbar();
  const showSuccessSnackbar = useCallback((msg) => enqueueSnackbar(msg, { variant: "success" }), [enqueueSnackbar]);
  const showErrorSnackbar = useCallback((msg) => enqueueSnackbar(msg, { variant: "error" }), [enqueueSnackbar]);
  const [payPeriodTotals, setPayPeriodTotals] = useState({
    totalAdditions: 0,
    totalDeductions: 0,
    totalEETaxes: 0,
    totalERTaxes: 0
  });

  const [payrollItems, setPayrollItems] = useState([]);

  const wageItems = useMemo(
    () => payrollItems.filter(x => Number(x.ItemType) === ITEM_TYPE.Wages),
    [payrollItems]
  );

  const addDedItems = useMemo(
    () => payrollItems.filter(
      x => Number(x.ItemType) === ITEM_TYPE.Additions || Number(x.ItemType) === ITEM_TYPE.Deductions
    ),
    [payrollItems]
  );

  const taxItems = useMemo(
    () => payrollItems.filter(
      x => Number(x.ItemType) === ITEM_TYPE.EmployeeTaxes || Number(x.ItemType) === ITEM_TYPE.EmployerTaxes
    ),
    [payrollItems]
  );
  // useEffect(() => {
  //     console.log("🔥 PayPeriod Totals:", payPeriodTotals);
  // }, [payPeriodTotals]);

  const [employeeTotalGross, setEmployeeTotalGross] = useState(0);

  // useEffect(() => {
  //     console.log("🔥 Total Gross in PayrollForm:", employeeTotalGross);
  // }, [employeeTotalGross]);

  const resetPayrollTableValues = useCallback(() => {
    // Clear payroll tables
    setFieldValue("wage", []);
    setFieldValue("addAndDeductions", []);
    setFieldValue("eeAndErTaxes", []);

    // Clear local payroll data
    setEmpPayrollDetails([]);
    setPayrollItems([]);

    // Reset totals
    setPayPeriodTotals({
      totalAdditions: 0,
      totalDeductions: 0,
      totalEETaxes: 0,
      totalERTaxes: 0,
    });

    setEmployeeTotalGross(0);
  }, [setFieldValue]);

  const voidDatePref = !!billEntryPreferences?.voidDate;

  const { getCleanedValues, validateForm } = usePayrollActions({ values, formValues, isDropdownSelected, showErrorSnackbar, setFieldError, setFieldTouched });

  const [openAddEmployee, setOpenAddEmployee] = useState(false);
  const [openAddJobTitle, setOpenAddJobTitle] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const payrollId = propPayrollId || values.payrollId || values.payRollID || row?.id || row?.raw?.PayrollID;
  const [, setSelectedEmployeeId] = useState("");

  const isReadOnly = Boolean(formValues?.reconStatus) || Boolean(formValues?.clearedDate) || Boolean(row?.raw?.createdDate) || Boolean(row?.raw?.CreatedDate);
  const isReportScreen = Boolean(popupID || payrollEmpId || row?.isReport || row?.fromReport);
  const isCompletedRecord = Boolean(
    row?.skipPayrollDetailsLoad ||
    row?.statusText === "Matched" ||
    row?.statusText === "Completed" ||
    row?.view === "Matched" ||
    row?.view === "Completed" ||
    String(row?.raw?.MappingStatus || "").trim().toLowerCase() === "matched" ||
    String(row?.raw?.MappingStatus || "").trim().toLowerCase() === "completed" ||
    row?.isCompleted ||
    row?.raw?.IsCompleted
  );
  const skipPayrollDetailsLoad = Boolean(isReportScreen || isCompletedRecord || row?.skipPayrollDetailsLoad);
  const [displayName, setDisplayName] = useState(null);

  const [isAddNewPayrollOpen, setIsAddNewPayrollOpen] = useState(false);
  const [prefillPayrollData, setPrefillPayrollData] = useState(null);
  const [reloadTrigger, setReloadTrigger] = useState(null);

  const [askAiData, setAskAiData] = useState([]);

  const corporationId =
    propCorporationId ||
    values.corporationId || "";

  const frequencyId =
    values?.frequencyId ||
    values?.FrequencyID ||
    formValues?.frequencyID ||
    "";

  const [loadedAccountOptions, setLoadedAccountOptions] = useState([]);
  useEffect(() => {
    if (propAccountOptions && propAccountOptions.length > 0) {
      setLoadedAccountOptions(propAccountOptions);
    } else if (corporationId) {
      getAccounts(corporationId).then((accs) => {
        setLoadedAccountOptions(accs || []);
      });
    }
  }, [propAccountOptions, corporationId]);
  const accountOptions = (propAccountOptions && propAccountOptions.length > 0) ? propAccountOptions : loadedAccountOptions;
  const { attachments, currentAttachmentIndex, setCurrentAttachmentIndex, currentFile, activeIframeSrc, showPreviewPanel } = usePayrollAttachments({ payrollId, values, formValues });


  const getIsType1 = useCallback(() => {
    const importedEmpName = row?.raw?.ImportedEmployeeName ?? row?.importedName ?? formValues?.importedName ?? values?.importedName;
    const isImportedNameEmpty = !importedEmpName || String(importedEmpName).trim() === "" || String(importedEmpName).toLowerCase() === "null" || String(importedEmpName).toLowerCase() === "undefined";
    return Boolean(
      isImportedNameEmpty ||
      values?.importPayrollType === 1 ||
      values?.isAddPayRoll ||
      values?.empType === 1 ||
      values?.EmpType === 1 ||
      row?.isAddPayRoll ||
      row?.raw?.ImportPayrollType === 1 ||
      row?.importPayrollType === 1 ||
      String(row?.entryType || "").toUpperCase() === "MANUAL" ||
      String(row?.raw?.EntryType || "").toUpperCase() === "MANUAL"
    );
  }, [row, formValues?.importedName, values?.importedName, values?.importPayrollType, values?.isAddPayRoll, values?.empType, values?.EmpType]);

  const handleAskAiClick = async () => {
    const isType1 = getIsType1();
    if (isType1 || isLoading) return;
    if (!formValues.department && !formValues.job) {
      showErrorSnackbar("Department and Job Title must be provided.");
      return;
    }
    if (!formValues.department) {
      showErrorSnackbar("Department must be provided.");
      return;
    }
    if (!formValues.job) {
      showErrorSnackbar("Job Title must be provided.");
      return;
    }

    prevTableValuesRef.current = {
      wage: valuesRef.current?.wage,
      addAndDeductions: valuesRef.current?.addAndDeductions,
      eeAndErTaxes: valuesRef.current?.eeAndErTaxes,
    };

    await runAskAi({ departmentId: formValues.department, jobId: formValues.job });
  };

  const handleOpenAddNewPayroll = (passedItemType, passedItemName, index, arrayName) => {
    const unmappedDetail = empPayrollDetails.find(d => !d.COA || !d.PayrollDetailID) || {};
    const itemTypeNum = (typeof passedItemType === 'number' || typeof passedItemType === 'string') ? passedItemType : unmappedDetail.ItemType || "";
    const itemName = (typeof passedItemName === 'string') ? passedItemName : unmappedDetail.ItemName || "";

    setPrefillPayrollData({
      corpId: values.corporationId,
      departmentId: formValues.department,
      jobId: formValues.job,
      itemName: itemName,
      itemTypeNumber: itemTypeNum,
      rowIndex: index,
      arrayName: arrayName,
      corpName: values?.corporation?.corpName || values?.corporation || ""
    });
    setIsAddNewPayrollOpen(true);
  };

  const displayHours = useCallback((val) => {
    if (values?.IsHoursDecimal) {
      const decimalVal = typeof val === 'string' && val.includes(':') ? parseHoursToDecimal(val) : val;
      return formatAmount(decimalVal);
    }
    return formatHours(val);
  }, [values?.IsHoursDecimal]); // eslint-disable-next-line


  const upsertEmployeeOption = useCallback((name, employeeId = "") => { // eslint-disable-next-line
    const trimmedName = String(name || "").trim();
    if (!trimmedName) return;
    const normalizedEmployeeId = String(employeeId || "").trim();

    setEmployeeOptions((prev) => {
      const existingIndex = prev.findIndex(
        (opt) =>
          String(opt?.value || "").trim().toLowerCase() ===
          trimmedName.toLowerCase()
      );

      if (existingIndex >= 0) {
        if (!normalizedEmployeeId || prev[existingIndex]?.employeeId) {
          return prev;
        }

        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          employeeId: normalizedEmployeeId,
        };
        return updated;
      }

      return [
        {
          label: trimmedName,
          value: trimmedName,
          employeeId: normalizedEmployeeId,
        },
        ...prev,
      ];
    });
  }, []);

  const initialPayrollStateRef = useRef(null);
  const prevDeptRef = useRef(formValues.department || "");
  const setInitialState = useCallback((state) => {
    initialPayrollStateRef.current = state;
    if (state?.formValues?.department) {
      prevDeptRef.current = state.formValues.department;
    }
  }, []);

  const {
    handleUpdate,
    handleDelete,
    handleCancel,
    updatingPayroll,
    setUpdatingPayroll,
    navigatingEmployee,
    rateChangeConfirm,
    setRateChangeConfirm,
  } = useEmployeePayrollMappingActions({
    row,
    values,
    formValues,
    payrollId,
    corporationId,
    isCompletedRecord,
    isVoid,
    voidDate,
    voidDatePref,
    comments,
    empPayrollDetails,
    employeeTotalGross,
    payPeriodTotals,
    permissions,
    popupID,
    setStep,
    setSelectedRow,
    setFieldValue,
    apiRatesRef,
    originalFormValuesRef,
    initialPayrollStateRef,
    hasCalledUnmatchedQueueRef,
    resetPayrollTableValues,
    validateForm,
    getCleanedValues,
    showErrorSnackbar,
    showSuccessSnackbar,
    showDeleteConfirm,
    showCancelConfirm,
    alertPopupOpen: alertPopup.open,
  });

  useEffect(() => {
    const fetchDropdownData = async () => {
      if (!corporationId) return;
      try {
        const [depts, jobs, profits, employeeNames] = await Promise.all([
          getDepartments(),
          getJobTitles(),
          getProfitCenters(corporationId),
          getEmpName(corporationId, frequencyId),
        ]);
        setDepartments(depts || []);
        setJobTitles([{ label: "Add New", value: "ADD_NEW" }, ...(jobs || [])]);
        setProfitCenters(profits || []);
        setEmployeeOptions(mapEmployeeNameOptions(employeeNames || []));
      } catch (error) {
        console.error("Failed to load initial dropdown data", error);
      }
    };
    fetchDropdownData();
  }, [corporationId, frequencyId]);

  useEffect(() => {
    const loadBillEntryPreferences = async () => {
      if (!corporationId) {
        setBillEntryPreferences({ voidDate: false, previewPanel: false });
        return;
      }
      try {
        const response = await getBillEntryPreferences(corporationId);
        setBillEntryPreferences({
          voidDate: !!response?.voidDate,
          previewPanel: !!response?.previewPanel,
        });
      } catch (error) {
        console.error("Error loading bill entry preferences:", error);
        setBillEntryPreferences({ voidDate: false, previewPanel: false });
      }
    };

    loadBillEntryPreferences();
  }, [corporationId]);

  const payrollHookProps = {
    // Core information
    row,
    values,
    corporationId,
    setReloadTrigger,

    departments,
    jobTitles,
    empPayrollDetails,
    setAskAiData,

    // Payroll screen state
    setLoadingDetails,
    setFormValues,
    setEmpPayrollDetails,
    setPayrollItems,

    // Void payroll information
    setIsVoid,
    setVoidDate,
    setComments,

    // Employee mapping information

    setSelectedEmployeeId,
    setDisplayName,
    setIsDropdownSelected,
    setManualDeptJobChange,

    // Ask AI controls
    setAskAiButtonVisible,
    setAskAiPopupOpen,

    // Formik helpers
    setFieldValue,

    // Utility methods
    displayHours,

    // References
    apiRatesRef,
    originalFormValuesRef,
    setInitialState,

    // Screen and status flags
    skipPayrollDetailsLoad,
    isReportScreen,
    isCompletedRecord,

    skipAskAiOnEmployeeChangeRef,
  };

  usePayrollEmployeeDetails(payrollHookProps);



  useEffect(() => {
    if (formValues.name) {
      upsertEmployeeOption(formValues.name, formValues.employeeId);
    }
  }, [formValues.name, formValues.employeeId, upsertEmployeeOption]);

  const runAskAi = useCallback(async ({ departmentId, jobId } = {}) => {
    if (getIsType1()) return;

    const activeDept = departmentId || formValues.department;
    const activeJob = jobId || formValues.job;

    if (!activeDept || !activeJob) return;

    setLoadingDetails(true);
    try {
      lastIsDeptJobRef.current = true;

      const deptLabel = departments.find(d => String(d.value) === String(activeDept))?.label || activeDept;
      const jobLabel = jobTitles.find(j => String(j.value) === String(activeJob))?.label || activeJob;

      const payload = {
        CorpID: values.corporationId || corporationId,
        PayrollEmpID: String(values.payrollEmpId || row?.raw?.PayrollEmpID || ""),
        DepartmentName: deptLabel,
        JobName: jobLabel,
        DepartmentID: activeDept,
        JobID: activeJob,
        IsDeptJob: true
      };

      const { dataList, manualItems, autoMapItems } = await fetchAISuggestions(payload);
      setAskAiData(dataList);

      let newWages = [...(valuesRef.current?.wage || [])];
      let newAddDeds = [...(valuesRef.current?.addAndDeductions || [])];
      let newTaxes = [...(valuesRef.current?.eeAndErTaxes || [])];

      const allSuggestionsToMap = (dataList && dataList.length > 0) ? dataList : (autoMapItems || []);

      if (allSuggestionsToMap && allSuggestionsToMap.length > 0) {
        const result = applyAISuggestions({
          suggestions: allSuggestionsToMap,
          wages: newWages,
          addDeds: newAddDeds,
          taxes: newTaxes,
          displayHours,
          isHoursDecimal: valuesRef.current?.IsHoursDecimal,
          overwriteExisting: true,
          clearCOA: false,
          payrollItems: payrollItems
        });

        newWages = result.wages;
        newAddDeds = result.addDeds;
        newTaxes = result.taxes;
      }

      setFieldValue("wage", newWages);
      setFieldValue("addAndDeductions", newAddDeds);
      setFieldValue("eeAndErTaxes", newTaxes);

      if (manualItems.length === 0) {
        setAskAiButtonVisible(false);
      } else {
        setAskAiButtonVisible(true);
        setAskAiData(dataList);
        setAskAiPopupOpen(true);
      }
    } catch (error) {
      console.error("Error checking AI suggestions:", error);
      setAskAiButtonVisible(false);
    } finally {
      setLoadingDetails(false);
    }
  }, [formValues.department, formValues.job, setFieldValue, values.corporationId, corporationId, values.payrollEmpId, row, departments, jobTitles, displayHours, payrollItems, getIsType1]);

  useEffect(() => {
    const isDeptOrJobManuallyChanged = manualDeptJobChange > lastManualDeptJobChangeRef.current;
    lastManualDeptJobChangeRef.current = manualDeptJobChange;

    if (!isDeptOrJobManuallyChanged) {
      return;
    }

    if (skipAskAiOnEmployeeChangeRef.current) {
      skipAskAiOnEmployeeChangeRef.current = false;
      return;
    }

        const isType1 = getIsType1();
    if (!isType1 && manualDeptJobChange > 0 && formValues.department && formValues.job) {
      prevDeptRef.current = formValues.department;
      runAskAi({ departmentId: formValues.department, jobId: formValues.job });
    }
  }, [manualDeptJobChange, formValues.department, formValues.job, getIsType1, runAskAi]);

  useEffect(() => {
    const isDeptEmpty = !formValuesRef.current.department;
    const isJobEmpty = !formValuesRef.current.job;

    shouldFetchDeptJobRef.current = isDeptEmpty && isJobEmpty;

    setManualDeptJobChange(0);
    lastManualDeptJobChangeRef.current = 0;
    prevDeptRef.current = formValuesRef.current.department || "";
    lastChangedFieldRef.current = null;
  }, [row?.id, values.payrollEmpId]);

  const handleVoidClick = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    if (voidDatePref) {
      const pDate = values.payDate ? dayjs(values.payDate) : null;
      const vDate = voidDate ? dayjs(voidDate) : null;
      if (vDate && pDate && vDate.isBefore(pDate, 'day')) {
        showErrorSnackbar("Void Date cannot be less than Pay Date.");
        return;
      }
    }

    setUpdatingPayroll(true);
    const cleanedValues = getCleanedValues(values);
    const payload = buildUpdatePayrollEmployeeMappingPayload({ ...formValues, bankAccountID: values.account }, cleanedValues, payrollId, corporationId, { ...row, raw: { ...row.raw, PayrollEmpID: values.payrollEmpId } }, true, voidDatePref ? voidDate : null, voidDatePref ? comments : null, empPayrollDetails, voidDatePref, employeeTotalGross, payPeriodTotals);

    try {
      const response = await saveOrEditPayrollEmpMapping(payload);
      if (response?.StatusCode === 200 || response?.Status === "Success") {
        // const voidPayload = buildUpdateVoidDatePayload(values.payrollEmpId,row, true, voidDatePref ? voidDate : null, voidDatePref ? comments : null, voidDatePref);
        // const voidResponse = await updateVoidDate(voidPayload);
        // if (voidResponse?.StatusCode === 200 || voidResponse?.Status === "Success") {
        //   showSuccessSnackbar("Employee payroll voided successfully.");
        //   if (popupID) {
        //       window.parent.postMessage(`popuphide|${popupID}`, "*");
        //   }
        //   setStep(2);
        // } else {
        //   throw new Error(voidResponse?.Status || "Failed to update void status.");
        // }
        // if (voidResponse?.StatusCode === 200 || voidResponse?.Status === "Success") {
        showSuccessSnackbar("Employee payroll voided successfully.");
        if (popupID) {
          window.parent.postMessage(`popuphide|${popupID}`, "*");
        }
        setStep(2);

      } else {
        throw new Error(response?.Status || "Failed to update.");
      }
    } catch (error) {
      showErrorSnackbar(error.message || "An error occurred while voiding.");
    } finally {
      setUpdatingPayroll(false);
    }
  }, [
    validateForm,
    voidDatePref,
    values,
    voidDate,
    showErrorSnackbar,
    formValues,
    payrollId,
    corporationId,
    row,
    comments,
    empPayrollDetails,
    employeeTotalGross,
    payPeriodTotals,
    setStep,
    showSuccessSnackbar,
    getCleanedValues,
    popupID,
    setUpdatingPayroll,
  ]);


  const handleAskAiApplySuccess = useCallback((allSuggestions, closePopup = true, payrollItems = []) => {
    const result = applyAISuggestions({
      suggestions: allSuggestions,
      wages: valuesRef.current?.wage || values?.wage || [],
      addDeds: valuesRef.current?.addAndDeductions || values?.addAndDeductions || [],
      taxes: valuesRef.current?.eeAndErTaxes || values?.eeAndErTaxes || [],
      displayHours,
      isHoursDecimal: valuesRef.current?.IsHoursDecimal ?? values?.IsHoursDecimal,
      overwriteExisting: true,
      clearCOA: false,
      payrollItems: payrollItems
    });

    setFieldValue("wage", result.wages);
    setFieldValue("addAndDeductions", result.addDeds);
    setFieldValue("eeAndErTaxes", result.taxes);

    if (closePopup) {
      setAskAiPopupOpen(false);
    }
  }, [setFieldValue, displayHours, values]);
  // --- Resizing Logic ---
  const { rightPanelWidth, containerRef, startResizing } = useResizablePanel();
  // ----------------------



  const handleChange = (field) => (event) => {
    const newValue = event.target.value;

    if (field === "checkNo") {
      if (!/^[a-zA-Z0-9]*$/.test(newValue)) {
        showErrorSnackbar("Special characters are not allowed in Cheque No.");
        return;
      }
    }

    
    if (field === "job" && newValue === "ADD_NEW") {
      setOpenAddJobTitle(true);
      return;
    }

    if (field === "department" || field === "job") {
      skipAskAiOnEmployeeChangeRef.current = false;
      setAskAiData([]);
      lastChangedFieldRef.current = field;
      prevTableValuesRef.current = {
        wage: valuesRef.current?.wage,
        addAndDeductions: valuesRef.current?.addAndDeductions,
        eeAndErTaxes: valuesRef.current?.eeAndErTaxes,
      };

      const isCurrentlyVoided = Boolean(
        isVoid ||
        formValues.isVoidStamp ||
        row?.raw?.IsActive === 2 ||
        row?.raw?.IsVoided
      );

      if (isCurrentlyVoided) {
        setIsVoid(false);
        setComments("");
        if (row) {
          row.statusText = "Unmapped";
          row.view = "Unmapped";
          row.isCompleted = false;
          row.isVoidStamp = false;
          if (row.raw) {
            row.raw.MappingStatus = "Unmapped";
            row.raw.IsActive = 1;
            row.raw.IsVoided = false;
          }
        }
        if (typeof setSelectedRow === "function") {
          setSelectedRow((prev) => ({
            ...prev,
            statusText: "Unmapped",
            view: "Unmapped",
            isCompleted: false,
            isVoidStamp: false,
            raw: {
              ...prev?.raw,
              MappingStatus: "Unmapped",
              IsActive: 1,
              IsVoided: false,
            },
          }));
        }
      }
    }



    setFormValues((prev) => ({
      ...prev,
      [field]: newValue,
      ...(field === "department" || field === "job" ? { isVoidStamp: false } : {}),
    }));
    if (field === "department" || field === "job") {
      setManualDeptJobChange((prev) => prev + 1);
    }
  };

  const handleNameChange = async (event, selectedOption = null) => {

    // Employee dropdown changed - skip automatic Ask AI API
    skipAskAiOnEmployeeChangeRef.current = true;

    const selectedValue = event?.target?.value || "";

    if (selectedValue === "ADD_NEW") {
      setSelectedEmployeeId("");
      setFormValues((prev) => ({
        ...prev,
        name: "",
        employeeId: "",
      }));
      setOpenAddEmployee(true);
      return;
    }

    const nextEmployeeId = String(selectedOption?.employeeId || "").trim();
    setSelectedEmployeeId(nextEmployeeId);

    setIsDropdownSelected(!!(selectedOption && nextEmployeeId));

    const isDeptEmptyOrSelect = (val) => {
      if (!val) return true;
      const s = String(val).trim().toLowerCase();
      return s === "" || s === "--select--" || s === "null" || s === "undefined";
    };

    const isJobEmptyOrSelect = (val) => {
      if (!val) return true;
      const s = String(val).trim().toLowerCase();
      return s === "" || s === "--select--" || s === "null" || s === "undefined";
    };

    const isCurrentDeptEmpty = isDeptEmptyOrSelect(formValues.department);
    const isCurrentJobEmpty = isJobEmptyOrSelect(formValues.job);

    const hasValidDept = Boolean(originalFormValuesRef.current?.hasInitialDept) && !isCurrentDeptEmpty;
    const hasValidJob = Boolean(originalFormValuesRef.current?.hasInitialJob) && !isCurrentJobEmpty;

    // If the response from GetPayrollEmployeeMapping had department OR job, and that field is not empty or "--Select--",
    // then on employee change do not call GetDeptJobDetails and keep department and job unchanged.
    // If neither was in the response OR if the present field(s) became empty/--Select--, fetch them via GetDeptJobDetails.
    const shouldPreserveDeptJob = hasValidDept || hasValidJob;

    if (shouldPreserveDeptJob) {
      setFormValues((prev) => ({
        ...prev,
        name: selectedValue,
        employeeId: nextEmployeeId,
      }));
      skipAskAiOnEmployeeChangeRef.current = false;
      return;
    }

    if (nextEmployeeId) {
      try {
        setLoadingDetails(true);
        const details = await getDeptJobDetails(nextEmployeeId);
        const deptJobData = details?.DeptJobs?.[0];
        if (deptJobData) {
          const newDept = deptJobData.IncDeptID || "";
          const newJob = deptJobData.JobID || "";
          const newPC = deptJobData.PCID || "";
          prevDeptRef.current = newDept;
          setFormValues((prev) => ({
            ...prev,
            name: selectedValue,
            employeeId: nextEmployeeId,
            department: newDept,
            job: newJob,
            profitCenter: newPC || prev.profitCenter || "",
          }));

          skipAskAiOnEmployeeChangeRef.current = false;
          if (getIsType1()) {
            setManualDeptJobChange((prev) => prev + 1);
          } else {
            if (newDept && newJob) {
              await runAskAi({ departmentId: newDept, jobId: newJob });
            } else {
              const finalDept = newDept || formValues.department;
              const finalJob = newJob || formValues.job;
              if (finalDept && finalJob) {
                await runAskAi({ departmentId: finalDept, jobId: finalJob });
              } else {
                setManualDeptJobChange((prev) => prev + 1);
              }
            }
          }
        } else {
          prevDeptRef.current = "";
          setFormValues((prev) => ({
            ...prev,
            name: selectedValue,
            employeeId: nextEmployeeId,
            department: "",
            job: "",
          }));
          if (nextEmployeeId) {
            setManualDeptJobChange((prev) => prev + 1);
          }
        }
      } catch (error) {
        console.error("Error fetching department/job details:", error);
        showErrorSnackbar("Could not fetch employee's department and job.");
        setFormValues((prev) => ({ ...prev, name: selectedValue, employeeId: nextEmployeeId }));
      } finally {
        setLoadingDetails(false);
        skipAskAiOnEmployeeChangeRef.current = false;
      }
    } else {
      setFormValues((prev) => ({ ...prev, name: selectedValue, employeeId: nextEmployeeId }));
      if (nextEmployeeId) {
        setManualDeptJobChange((prev) => prev + 1);
      }
    }
  };

  const handleAddEmployeeSuccess = async ({ payload, fullName }) => {
    try {
      const [employeeNames, depts, jobs] = await Promise.all([
        getEmpName(corporationId, frequencyId),
        getDepartments(),
        getJobTitles(),
      ]);
      const options = mapEmployeeNameOptions(employeeNames || []);
      setEmployeeOptions(options);
      if (depts) setDepartments(depts);
      if (jobs) setJobTitles([{ label: "Add New", value: "ADD_NEW" }, ...(jobs || [])]);

      const resolvedName = String(fullName || "").trim();
      const fallbackName = [payload?.FirstName, payload?.MiddleName, payload?.LastName]
        .filter(Boolean)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();
      const employeeName = resolvedName || fallbackName;
      const newEmployeeId = String(payload?.EmployeeID || payload?.EmployeeId || "").trim();

      let deptJobData = null;
      let deptJobDetails = null;

      const isDeptEmptyOrSelect = (val) => {
        if (!val) return true;
        const s = String(val).trim().toLowerCase();
        return s === "" || s === "--select--" || s === "null" || s === "undefined";
      };

      const isJobEmptyOrSelect = (val) => {
        if (!val) return true;
        const s = String(val).trim().toLowerCase();
        return s === "" || s === "--select--" || s === "null" || s === "undefined";
      };

      const isCurrentDeptEmpty = isDeptEmptyOrSelect(formValues.department);
      const isCurrentJobEmpty = isJobEmptyOrSelect(formValues.job);

      const hasValidDept = Boolean(originalFormValuesRef.current?.hasInitialDept) && !isCurrentDeptEmpty;
      const hasValidJob = Boolean(originalFormValuesRef.current?.hasInitialJob) && !isCurrentJobEmpty;

      const shouldPreserveDeptJob = hasValidDept || hasValidJob;

      if (newEmployeeId && !shouldPreserveDeptJob) {
        try {
          deptJobDetails = await getDeptJobDetails(newEmployeeId);
          deptJobData = deptJobDetails?.DeptJobs?.[0];
        } catch (error) {
          console.error("Error fetching department/job details:", error);
        }
      }

      const firstPayrollRow = payload?.payrollRows?.[0];
      const isPayrollRowsProvided = Array.isArray(payload?.payrollRows);
      const isDeptOrJobRemoved = isPayrollRowsProvided && (isDeptEmptyOrSelect(firstPayrollRow?.department) || isJobEmptyOrSelect(firstPayrollRow?.jobTitle));

      const newDept = shouldPreserveDeptJob ? formValues.department : (deptJobData?.IncDeptID || firstPayrollRow?.department || payload?.IncDeptID || payload?.department || (isDeptOrJobRemoved ? "" : formValues.department));
      const newJob = shouldPreserveDeptJob ? formValues.job : (deptJobData?.JobID || firstPayrollRow?.jobTitle || payload?.JobID || payload?.jobTitle || payload?.job || (isDeptOrJobRemoved ? "" : formValues.job));
      const newPC = shouldPreserveDeptJob ? formValues.profitCenter : (deptJobData?.PCID || firstPayrollRow?.profitCenter || payload?.PCID || formValues.profitCenter);

      const matchedEmp = options.find((opt) => opt.employeeId && String(opt.employeeId) === newEmployeeId) ||
        options.find((opt) => opt.label.toLowerCase() === employeeName.toLowerCase());

      const finalName = matchedEmp?.value || employeeName || formValues.name;

      if (employeeName && !matchedEmp) {
        upsertEmployeeOption(employeeName, newEmployeeId);
      }

      setSelectedEmployeeId(newEmployeeId || matchedEmp?.employeeId || "");
      setIsDropdownSelected(true);

      setFormValues((prev) => ({
        ...prev,
        name: finalName,
        department: newDept,
        job: newJob,
        profitCenter: newPC,
        employeeId: newEmployeeId || matchedEmp?.employeeId || prev.employeeId,
      }));
      prevDeptRef.current = newDept || "";

      skipAskAiOnEmployeeChangeRef.current = false;
      if (getIsType1()) {
        setManualDeptJobChange((prev) => prev + 1);
      } else {
        if (!isDeptOrJobRemoved && newDept && newJob) {
          await runAskAi({ departmentId: newDept, jobId: newJob });
        } else {
          skipAskAiOnEmployeeChangeRef.current = true;
          setManualDeptJobChange((prev) => prev + 1);
        }
      }
    } catch (error) {
      console.error("Failed to fetch employee names", error);

      // Fallback
      const resolvedName = String(fullName || "").trim();
      const fallbackName = [payload?.FirstName, payload?.MiddleName, payload?.LastName]
        .filter(Boolean)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();
      const employeeName = resolvedName || fallbackName;
      const newEmployeeId = String(payload?.EmployeeID || payload?.EmployeeId || "").trim();

      if (employeeName) {
        upsertEmployeeOption(employeeName, newEmployeeId);
      }
      setIsDropdownSelected(true);

      const firstPayrollRow = payload?.payrollRows?.[0];
      const newDept = firstPayrollRow?.department || payload?.IncDeptID || payload?.department || formValues.department;
      const newJob = firstPayrollRow?.jobTitle || payload?.JobID || payload?.jobTitle || payload?.job || formValues.job;
      const newPC = firstPayrollRow?.profitCenter || payload?.PCID || formValues.profitCenter;

      setFormValues((prev) => ({
        ...prev,
        name: employeeName || prev.name,
        department: newDept,
        job: newJob,
        profitCenter: newPC,
        employeeId: newEmployeeId || prev.employeeId,
      }));
      setSelectedEmployeeId(newEmployeeId);
      skipAskAiOnEmployeeChangeRef.current = true;
      setManualDeptJobChange((prev) => prev + 1);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    if (typeof dateStr === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr.trim())) {
      return dateStr.trim();
    }
    const d = dayjs(dateStr);
    return d.isValid() ? d.format("MM/DD/YYYY") : String(dateStr);
  };

  const getAccountLabel = () => {
    if (!values?.account) return "";
    const found = accountOptions?.find(opt => opt.value === values.account);
    return found ? found.label : values.account;
    // console.log("account options", accountOptions, values?.account);
  };

  const getFrequencyString = () => {
    return `${formatDate(values?.payFrom)} - ${formatDate(values?.payTo)} (${values?.frequency || ""})`;
  };

  const nameOptions =
    [
      { label: "--Select--", value: "" },
      { label: "Add New Employee", value: "ADD_NEW" },
    ].concat(
      employeeOptions.length > 0
        ? employeeOptions
        : formValues.name
          ? [{ label: formValues.name, value: formValues.name, employeeId: formValues.employeeId }]
          : []
    );

  const isLoading = loadingDetails || updatingPayroll || navigatingEmployee;

  const jobTitleName = jobTitles.find(j => j.value === formValues.job)?.label || formValues.job;
  const departmentName = departments.find(d => d.value === formValues.department)?.label || formValues.department;

  const showAskAiButton = askAiButtonVisible;

  const payrollContext = {
    formValues,
    handleChange,
    handleNameChange,
    displayName,
    nameOptions,
    departments,
    jobTitles,
    profitCenters,
    row,
    values,
    isVoid,
    setIsVoid,
    voidDate,
    setVoidDate,
    comments,
    setComments,
    voidDatePref,
    handleVoidClick,
    showErrorSnackbar,
    employeeTotalGross,
    payPeriodTotals,
    isReadOnly,
    onAskAiClick: handleAskAiClick,
    showAskAiButton,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {isLoading && <Loader loading={isLoading} />}
      <Box
        sx={{
          width: "100%",
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header Bar */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 1 }}
          gap={2}
        >
          <Box
            sx={{
              bgcolor: "#eaf5fc",
              borderRadius: 1,
              px: 2,
              py: 1,
              flex: 1,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
            >
              <Typography fontSize={"0.7rem"} fontWeight={600} noWrap>
                Corporation Name: {formValues.corporationName || values?.corporation?.corpName || values?.corporation}
              </Typography>
              <Typography fontSize={"0.7rem"} fontWeight={600} noWrap>
                Pay Frequency: {formValues.payFrequencyDate || formValues.payFrequency || getFrequencyString()}
              </Typography>
              <Typography fontSize={"0.7rem"} fontWeight={600} noWrap>
                Pay Date: {formatDate(formValues.payDate || values?.payDate)}
              </Typography>
              <Typography fontSize={"0.7rem"} fontWeight={600} noWrap>
                Bank Account: {formValues.bankAccount || getAccountLabel()}
              </Typography>
            </Stack>
          </Box>

          <EmployeePayrollMappingActionButtons
            onClose={onClose}
            handleUpdate={handleUpdate}
            handleDelete={handleDelete}
            handleCancel={handleCancel}
            updatingPayroll={updatingPayroll}
            rateChangeConfirm={rateChangeConfirm}
            setRateChangeConfirm={setRateChangeConfirm}
          />
        </Stack>

        <Stack
          ref={containerRef}
          direction="row"
          sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}
        >
          {/* Left Section */}
          <Stack
            gap={2}
            p={2}
            bgcolor="#eaf5fc"
            sx={{ overflowY: "auto", height: "100%", width: showPreviewPanel ? `${100 - rightPanelWidth}%` : "100%" }}
          >
            <PayrollFormProvider value={payrollContext}>
              <PayPeriodFormInfo />
            </PayrollFormProvider>
            <EmployeeWageMappingTable
              row={row}
              wageItems={wageItems}
              setLoadingDetails={setLoadingDetails}
              accountOptions={[{ label: "-Select-", value: "", type: "" }, ...accountOptions]}
              formValues={formValues}
              corporationId={corporationId}
              onTotalGrossChange={setEmployeeTotalGross}
              onAddNewMappedItem={handleOpenAddNewPayroll}
              refreshTrigger={reloadTrigger}
              manualDeptJobChange={manualDeptJobChange}
              isReadOnly={isReadOnly}
              skipPayrollDetailsLoad={skipPayrollDetailsLoad}
              isReportScreen={isReportScreen}
              isCompletedRecord={isCompletedRecord}
              reloadTrigger={reloadTrigger}
              setInitialState={setInitialState}
            />

            <EmployeeAddDeductTaxTables
              row={row}
              addDedItems={addDedItems}
              taxItems={taxItems}
              accountOptions={[{ label: "--Select--", value: "", type: "" }, ...accountOptions]}
              formValues={formValues}
              corporationId={corporationId}
              onAddNewMappedItem={handleOpenAddNewPayroll}
              refreshTrigger={reloadTrigger}
              onTotalsChange={setPayPeriodTotals}
              isReadOnly={isReadOnly}
              skipPayrollDetailsLoad={skipPayrollDetailsLoad}
            />
          </Stack>

          {/* Resizer */}
          {showPreviewPanel && (
            <Box
              onMouseDown={startResizing}
              sx={{
                width: "10px",
                cursor: "col-resize",
                bgcolor: "#f0f0f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderLeft: "1px solid #e0e0e0",
                borderRight: "1px solid #e0e0e0",
                "&:hover": {
                  bgcolor: "#e0e0e0",
                },
              }}
            >
              <Box sx={{ width: "2px", height: "20px", bgcolor: "#bdbdbd", mx: "1px" }} />
            </Box>
          )}

          {/* Right Section */}
          {showPreviewPanel && (
            <Box
              sx={{
                height: "100%",
                width: `${rightPanelWidth}%`,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {attachments.length > 1 && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1, py: 0.5, bgcolor: "#f5f5f5", borderBottom: "1px solid #e0e0e0" }}>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={currentAttachmentIndex === 0}
                    onClick={() => setCurrentAttachmentIndex(prev => prev - 1)}
                    startIcon={<ArrowLeftTwoToneIcon />}
                    sx={{
                      textTransform: "none",
                      bgcolor: "#3D5C7B",
                      color: "white",
                      fontSize: 12,
                      "& .MuiButton-startIcon": {
                        marginRight: "0", // default is 8px
                      },
                    }}
                  >
                    Previous
                  </Button>
                  <Typography variant="caption" fontWeight={500}>
                    {currentAttachmentIndex + 1} of {attachments.length} - {currentFile?.Filename || currentFile?.FileName || "Document"}
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={currentAttachmentIndex === attachments.length - 1}
                    onClick={() => setCurrentAttachmentIndex(prev => prev + 1)}
                    endIcon={<ArrowRightTwoToneIcon />}
                    sx={{
                      textTransform: "none",
                      bgcolor: "#3D5C7B",
                      color: "white",
                      fontSize: 12,
                      "& .MuiButton-endIcon": {
                        marginLeft: "0", // default is 8px
                      },
                    }}
                  >
                    Next
                  </Button>
                </Stack>
              )}
              <iframe
                src={activeIframeSrc}
                style={{ width: "100%", height: "100%", border: "none", flexGrow: 1 }}
                title="Payroll Preview"
              />
            </Box>
          )}
        </Stack>
        <PayrollsPopup
          open={alertPopup.open}
          popupType={alertPopup.type}
          message={alertPopup.message}
          onConfirm={alertPopup.onConfirm}
          onCancel={hidePopup}
        />
        <AddNewEmployee
          open={openAddEmployee}
          onClose={() => setOpenAddEmployee(false)}
          onSuccess={handleAddEmployeeSuccess}
          corporationId={values.corporationId}
          corporationName={values?.corporation}
          employeeName={formValues.name || row?.name || ""}
          // payFrequency={values?.frequency}
          payFrequency={{ label: values?.frequency, value: values?.frequencyId }}
          payrollItemDeptJobId={
            row?.raw?.PayrollItemDeptJobId ||
            row?.raw?.PayrollItemDeptJobID ||
            row?.raw?.PayrollItemDeptJobDetailsID ||
            0
          }
          departments={departments}
          jobTitles={jobTitles}
          profitCenters={profitCenters}
          defaultDeptId={formValues.department}
          defaultJobId={formValues.job}
          profitCenterId={formValues.profitCenter}
          isQuickAdd={true}
        />
        <AddJobTitle
          open={openAddJobTitle}
          onClose={() => setOpenAddJobTitle(false)}
          onSuccess={async (createdJobTitle) => {
            try {
              const jobs = await getJobTitles();
              setJobTitles([{ label: "Add New", value: "ADD_NEW" }, ...(jobs || [])]);

              const createdId = createdJobTitle?.value ? String(createdJobTitle.value) : "";
              const createdName = (createdJobTitle?.label || "").trim().toLowerCase();

              const matchedTitle =
                (jobs || []).find((item) => String(item.value) === createdId) ||
                (jobs || []).find((item) => (item.label || "").trim().toLowerCase() === createdName);

              if (matchedTitle?.value) {
                const isCurrentlyVoided = Boolean(
                  isVoid ||
                  formValues.isVoidStamp ||
                  row?.raw?.IsActive === 2 ||
                  row?.raw?.IsVoided
                );

                if (isCurrentlyVoided) {
                  setIsVoid(false);
                  setComments("");
                  if (row) {
                    row.statusText = "Unmapped";
                    row.view = "Unmapped";
                    row.isCompleted = false;
                    row.isVoidStamp = false;
                    if (row.raw) {
                      row.raw.MappingStatus = "Unmapped";
                      row.raw.IsActive = 1;
                      row.raw.IsVoided = false;
                    }
                  }
                  if (typeof setSelectedRow === "function") {
                    setSelectedRow((prev) => ({
                      ...prev,
                      statusText: "Unmapped",
                      view: "Unmapped",
                      isCompleted: false,
                      isVoidStamp: false,
                      raw: {
                        ...prev?.raw,
                        MappingStatus: "Unmapped",
                        IsActive: 1,
                        IsVoided: false,
                      },
                    }));
                  }
                }

                setFormValues((prev) => ({
                  ...prev,
                  job: matchedTitle.value,
                  isVoidStamp: false,
                }));
                setManualDeptJobChange((prev) => prev + 1);
              }
            } catch (err) {
              console.error("Failed to fetch job titles", err);
            }
          }}
        />
        <AddNewPayroll
          open={isAddNewPayrollOpen}
          onClose={() => setIsAddNewPayrollOpen(false)}
          prefillData={prefillPayrollData}
          onSuccess={async (newId) => {

            const prNamesPayload = {
              CorpID: values.corporationId,
              itemTypes: [ITEM_TYPE.Wages, ITEM_TYPE.Additions, ITEM_TYPE.Deductions, ITEM_TYPE.EmployeeTaxes, ITEM_TYPE.EmployerTaxes]
            };

            const payrollNames = await getPayrollItemNames(prNamesPayload);

            setPayrollItems(payrollNames?.Items || []);

            setIsAddNewPayrollOpen(false);

            setReloadTrigger({
              id: newId,
              rowIndex: prefillPayrollData?.rowIndex,
              arrayName: prefillPayrollData?.arrayName,
              trigger: Date.now()
            });
          }}
        />

        {askAiPopupOpen && (
          <ASKAIpopup
            open={askAiPopupOpen}
            onClose={(wasApplied) => {
              if (!wasApplied) {
                setAskAiButtonVisible(true);
              }
              prevTableValuesRef.current = null;
              setAskAiPopupOpen(false);
            }}
            onApplySuccess={async (allSuggestions, closePopup) => {
              prevTableValuesRef.current = null;

              const prNamesPayload = {
                CorpID: values.corporationId || corporationId,
                itemTypes: [
                  ITEM_TYPE.Wages,
                  ITEM_TYPE.Additions,
                  ITEM_TYPE.Deductions,
                  ITEM_TYPE.EmployeeTaxes,
                  ITEM_TYPE.EmployerTaxes
                ]
              };

              const payrollNames = await getPayrollItemNames(prNamesPayload);

              const newPayrollItems = payrollNames?.Items || [];
              setPayrollItems(newPayrollItems);

              setAskAiData(allSuggestions || []);
              handleAskAiApplySuccess(allSuggestions, closePopup, newPayrollItems);
              setAskAiButtonVisible(false);
            }}
            suggestionsData={askAiData}
            row={{
              jobTitleName: jobTitleName,
              departmentName: departmentName,
              CorpID: values.corporationId || corporationId,
              PayrollEmpID: values.payrollEmpId || row?.raw?.PayrollEmpID || row?.raw?.PayrollEmpId || "",
              CorpName: values?.corporation?.corpName || values?.corporation || "",
              DeptID: formValues.department,
              JobID: formValues.job,
              initialDept: originalFormValuesRef.current?.department,
              initialJob: originalFormValuesRef.current?.job,
              IsDeptJob: true
            }}
          />
        )}
      </Box>
    </LocalizationProvider >
  );
}
